import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Jurisdiction, JurisdictionSelector, Market } from "@obg/ngx.core";
import { ignoreNil, LocationService } from "@obg/ngx.global";
import { head } from "lodash-es";
import { combineLatest, Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";

import { ContentClient } from "../content/content.client";
import { CoreContentState } from "../state";

import { Flag, FlagResponse, RawFlag, UpdatePayload } from "./flag.model";
import { FlagUtil } from "./flag.util";

@Injectable({
	providedIn: "root",
})
export class FlagService {
	constructor(
		private client: ContentClient,
		private locationService: LocationService,
		private store: Store<CoreContentState>
	) {}

	/**
	 * @deprecated [fabric-deprecated] - Use "document.obgEventBus.retrieve(ContentValue.getCurrentFlag_v2)" instead
	 */
	getAll$(): Observable<Flag[]> {
		return combineLatest([
			this.client.getFlags().pipe(startWith({ id: "", pages: [] } as FlagResponse)),
			this.store.select(JurisdictionSelector.get).pipe(ignoreNil(), startWith(Jurisdiction.Mga)),
		]).pipe(
			map(([result, jurisdiction]) => {
				const flags = result.pages.map(this.processRaw);
				return this.filterFlagsByJurisdiction(flags, jurisdiction);
			})
		);
	}

	getById(flags: Flag[], id: string): Flag | undefined {
		return flags.find((flag) => flag.id === id);
	}

	getByMarket(flags: Flag[], currentMarket: Market | undefined): Flag | undefined {
		return flags.find(
			(flag) => flag.marketCode === currentMarket?.urlSlug || flag.marketCode === currentMarket?.languageCode
		);
	}

	appendHrefToFlags(updatePayload: UpdatePayload): Flag[] {
		const { flags, queryParams, urls } = updatePayload;

		return flags.map((flag) => {
			const alternativeCultureUrl = FlagUtil.getAlternativeCultureUrl(flag, urls);
			const fallbackHref = this.locationService.getOrigin();
			const href = FlagUtil.getHref({ flag, alternativeCultureUrl, fallbackHref });

			return !FlagUtil.isExternalDomain(flag) && queryParams
				? { ...flag, href: FlagUtil.addQueryParamsToHref(href, queryParams) }
				: { ...flag, href };
		});
	}

	private filterFlagsByJurisdiction(flags: Flag[], jurisdiction: Jurisdiction): Flag[] {
		return flags.filter(
			(flag) => !flag.jurisdiction?.length || flag.jurisdiction.some((item) => item === jurisdiction)
		);
	}

	private processRaw(rawFlagItem: RawFlag): Flag {
		const imageContentItems = rawFlagItem.content.imageContentItems;
		const jurisdiction = rawFlagItem.content.metadata?.jurisdiction;

		return {
			id: rawFlagItem.documentKey,
			name: head(rawFlagItem.content.textContentItems.name)!,
			languageCode: head(rawFlagItem.content.textContentItems.languageCode)!,
			countryCode: head(rawFlagItem.content.textContentItems.countryCode)!,
			marketCode: head(rawFlagItem.content.textContentItems.marketCode)!,
			iconTag: head(rawFlagItem.content.textContentItems.iconTag)!,
			link: head(rawFlagItem.content.linkContentItems.url)!,
			image: imageContentItems && head(imageContentItems.image),
			jurisdiction,
		};
	}
}
